/**
 * Digit-based input formatters for masked fields. Each `format*` takes whatever
 * the user has typed, keeps only digits, truncates to the field's max, and
 * regroups with separators — so it's idempotent (re-running on already-formatted
 * text is a no-op) and backspace-friendly (separators disappear once the digit
 * after them is gone). Values are stored formatted; the backend strips non-digits
 * where it matters (see apps/clients/serializers.py `_digits`).
 */

export function onlyDigits(value: string): string {
  return (value || "").replace(/\D/g, "");
}

/** `123456789` / `123-45-6789` → `123-45-6789` (SSN/ITIN). */
export function formatSSN(raw: string): string {
  const d = onlyDigits(raw).slice(0, 9);
  let out = d.slice(0, 3);
  if (d.length > 3) out += `-${d.slice(3, 5)}`;
  if (d.length > 5) out += `-${d.slice(5, 9)}`;
  return out;
}

/** `123456789` → `12-3456789` (EIN). */
export function formatEIN(raw: string): string {
  const d = onlyDigits(raw).slice(0, 9);
  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
}

/** `1234567890` → `(123) 456-7890` (US phone). */
export function formatPhone(raw: string): string {
  const d = onlyDigits(raw).slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** `12345` → `12345`, `123456789` → `12345-6789` (US ZIP / ZIP+4). */
export function formatZip(raw: string): string {
  const d = onlyDigits(raw).slice(0, 9);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** `08252026` → `08/25/2026` (date). */
export function formatDate(raw: string): string {
  const d = onlyDigits(raw).slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length > 2) out += `/${d.slice(2, 4)}`;
  if (d.length > 4) out += `/${d.slice(4, 8)}`;
  return out;
}

/** `0930` → `09:30` (24-hour time). */
export function formatTime(raw: string): string {
  const d = onlyDigits(raw).slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
}

// Completeness regexes for Yup — validate the *formatted* value the field stores.
export const SSN_RE = /^\d{3}-\d{2}-\d{4}$/;
export const EIN_RE = /^\d{2}-\d{7}$/;
export const PHONE_RE = /^\(\d{3}\) \d{3}-\d{4}$/;
export const ZIP_RE = /^\d{5}(-\d{4})?$/;
// Enforces month 01-12 and day 01-31 (not day-per-month — use maskedDateToISO
// for a full calendar check).
export const DATE_RE = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * `MM/DD/YYYY` → `YYYY-MM-DD` for API submission; `""` if not a real calendar
 * date. A round-trip through `Date` rejects impossibilities the regex can't
 * (e.g. `02/30/2026`, `04/31/2026`).
 */
export function maskedDateToISO(masked: string): string {
  if (!DATE_RE.test(masked)) return "";
  const [mm, dd, yyyy] = masked.split("/");
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`);
  const valid =
    d.getUTCFullYear() === Number(yyyy) &&
    d.getUTCMonth() + 1 === Number(mm) &&
    d.getUTCDate() === Number(dd);
  return valid ? `${yyyy}-${mm}-${dd}` : "";
}

/** `YYYY-MM-DD` → `MM/DD/YYYY` for prefilling a DateField; `""` if malformed. */
export function isoToMaskedDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
}
